import {
  PipelineStateSchema,
  UnmaskParticipantBodySchema,
  type PipelineState,
} from '@pin/schemas/admin';
import { firstValueFrom } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { timeout } from 'rxjs/operators';
import { withIdToken } from '../firebase/auth';
import { adminApiUrl } from '../utils/context';
import { HttpError } from '../utils/rxjs.http';

export type StartPipelineResult =
  { started: true; statusUrl: string } | { started: false; error: string };

export const startPipeline = (
  participantId: string,
  unmaskedName: string
): Promise<StartPipelineResult> => {
  const searchParams = new URLSearchParams({});
  if (import.meta.env.DEV) {
    searchParams.append('includeTestUsers', 'true');
  }
  return firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${adminApiUrl}/api/pipeline/${participantId}?${searchParams}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(
            UnmaskParticipantBodySchema.parse({ unmaskedName })
          ),
          selector: async (res) => {
            if (res.status === 202) {
              const statusUrl = res.headers.get('Location') ?? '';
              return { started: true, statusUrl } as const;
            }
            if (res.status === 409 || res.status === 422) {
              const body = (await res.json()) as {
                error: string;
                message: string;
              };
              return { started: false, error: body.message } as const;
            }
            throw new HttpError(res);
          },
        }
      ).pipe(timeout(10_000))
    )
  );
};

export const getPipelineStatus = (
  participantId: string
): Promise<PipelineState> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/pipeline/${participantId}/status`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        selector: async (res) => {
          if (!res.ok) throw new HttpError(res);
          return PipelineStateSchema.parse(await res.json());
        },
      }).pipe(timeout(10_000))
    )
  );
